/* S3 클라이언트 — 순수(React 비의존). plan.md §6 / docs/ops/code-registry.md.
   ★ 동결 인터페이스(S2~S4 변경 금지). getCredentials = session.getCredentials.
       getJsonAnon(url) => Promise<any>                              // 익명 GET (manifest 읽기)
       putObject(getCredentials, key, body, contentType) => Promise<void>
       deleteObject(getCredentials, key) => Promise<void>
       listPrefix(getCredentials, prefix) => Promise<string[]>

   S1 = fake 구현(localStorage 를 가짜 S3 버킷으로). SDK import 없음.
   S4 = real 구현(이 파일) — @aws-sdk/client-s3 + STS 임시자격(SigV4) 쓰기,
        익명 fetch GET(공식 manifest 는 public-read). 위 시그니처는 유지하고
        fake 분기(isFakeMode)는 보존한다(env 없으면 오프라인 데모로 동작). */
import { isFakeMode, CODE_BUCKET, CODE_BUCKET_URL, AWS_REGION } from '../config/env.js';
import {
  S3Client,
  PutObjectCommand,
  DeleteObjectCommand,
  ListObjectsV2Command,
} from '@aws-sdk/client-s3';

const S3_KEY = 'adm.fake.s3';   // { "<key>": { body, contentType } }
const delay = (ms = 160) => new Promise(r => setTimeout(r, ms));

function loadBucket() {
  try { return JSON.parse(localStorage.getItem(S3_KEY) || '{}'); } catch (e) { return {}; }
}
function saveBucket(b) { try { localStorage.setItem(S3_KEY, JSON.stringify(b)); } catch (e) { /* ignore */ } }

// 버킷 URL → 객체 key 추출 (fake 매핑). 절대/상대 URL 모두 허용.
function keyFromUrl(url) {
  try {
    if (CODE_BUCKET_URL && url.startsWith(CODE_BUCKET_URL)) return url.slice(CODE_BUCKET_URL.length).replace(/^\//, '');
    return new URL(url).pathname.replace(/^\//, '');
  } catch (e) {
    return String(url).replace(/^\//, '');
  }
}

// 직렬화 — fake 는 localStorage 라 문자열만 저장. 바이너리는 마커로 대체.
function serialize(body) {
  if (typeof body === 'string') return body;
  if (body && typeof body === 'object' && !ArrayBuffer.isView(body) && !(body instanceof ArrayBuffer) && !(typeof Blob !== 'undefined' && body instanceof Blob)) {
    try { return JSON.stringify(body); } catch (e) { return '[object]'; }
  }
  return '[binary]';
}

/* ---------- real(live) 헬퍼 ---------- */
// getCredentials() 는 session.getCredentials — STS 임시자격({accessKeyId,
// secretAccessKey,sessionToken,region}) 또는 null(미로그인). null 이면 명확히 실패.
async function clientFor(getCredentials) {
  const creds = typeof getCredentials === 'function' ? await getCredentials() : getCredentials;
  if (!creds || !creds.accessKeyId) {
    throw new Error('로그인이 필요합니다 — S3 자격증명을 가져오지 못했습니다.');
  }
  return new S3Client({
    region: creds.region || AWS_REGION,
    credentials: {
      accessKeyId: creds.accessKeyId,
      secretAccessKey: creds.secretAccessKey,
      sessionToken: creds.sessionToken,
    },
  });
}

// PutObject Body 정규화 — 문자열/바이너리는 그대로, 평문 객체는 JSON 직렬화.
function toBody(body) {
  if (body == null) return '';
  if (typeof body === 'string') return body;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return body;
  if (body instanceof ArrayBuffer) return new Uint8Array(body);
  if (ArrayBuffer.isView(body)) return body;
  return JSON.stringify(body);
}

/* ---------- API ---------- */
export async function getJsonAnon(url) {
  if (isFakeMode) {
    await delay();
    const bucket = loadBucket();
    const entry = bucket[keyFromUrl(url)];
    if (!entry) { const err = new Error('Not Found'); err.status = 404; throw err; }
    return JSON.parse(entry.body);
  }
  // live: 공식/공유 manifest 는 public-read → 비서명 GET. RMW 정확도를 위해 no-store.
  const res = await fetch(url, { cache: 'no-store' });
  if (res.status === 404 || res.status === 403) {
    const err = new Error('Not Found'); err.status = res.status; throw err;
  }
  if (!res.ok) {
    const err = new Error(`GET 실패: ${res.status}`); err.status = res.status; throw err;
  }
  return res.json();
}

export async function putObject(getCredentials, key, body, contentType) {
  if (isFakeMode) {
    await delay();
    const bucket = loadBucket();
    bucket[key] = { body: serialize(body), contentType: contentType || 'application/octet-stream' };
    saveBucket(bucket);
    return;
  }
  const client = await clientFor(getCredentials);
  await client.send(new PutObjectCommand({
    Bucket: CODE_BUCKET,
    Key: key,
    Body: toBody(body),
    ContentType: contentType || 'application/octet-stream',
  }));
}

export async function deleteObject(getCredentials, key) {
  if (isFakeMode) {
    await delay();
    const bucket = loadBucket();
    delete bucket[key];
    saveBucket(bucket);
    return;
  }
  const client = await clientFor(getCredentials);
  await client.send(new DeleteObjectCommand({ Bucket: CODE_BUCKET, Key: key }));
}

export async function listPrefix(getCredentials, prefix) {
  if (isFakeMode) {
    await delay();
    const bucket = loadBucket();
    return Object.keys(bucket).filter(k => k.startsWith(prefix));
  }
  const client = await clientFor(getCredentials);
  const keys = [];
  let token;
  do {
    const r = await client.send(new ListObjectsV2Command({
      Bucket: CODE_BUCKET, Prefix: prefix, ContinuationToken: token,
    }));
    (r.Contents || []).forEach(o => { if (o.Key) keys.push(o.Key); });
    token = r.IsTruncated ? r.NextContinuationToken : undefined;
  } while (token);
  return keys;
}
